import { useCallback, useEffect, useState } from 'react'
import { find, map } from 'lodash'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Icon } from '@/common/components/Icon'
import { ActionsMenu } from '@/components/ActionsMenu'
import Loader from '../../common/components/Loader'
import { IColumn, Table } from '../../core/components/Table'
import { useCheckPermission } from '../../core/hooks/use-check-permission'
import {
  deleteCampaign,
  getCampaigns,
  ICampaign,
} from '../../core/services/campaign'
import { CreateCampaignModal } from './CreateCampaignModal'
import { DeleteConfirmationModal } from './DeleteConfirmationModal'
import { MessageBox } from './messages/MessageBox'

export const CampaignsPage: React.FC = () => {
  const { hasEditPermission } = useCheckPermission({ page: 'CAMPAIGNS' })

  const [campaigns, setCampaigns] = useState<ICampaign[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const [showCreateCampaignModal, setShowCreateCampaignModal] =
    useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [currentCampaign, setCurrentCampaign] = useState<ICampaign>()

  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  const showFeedback = (error: boolean, msg: string, status?: number) => {
    setIsError(error)
    setMessage(msg)
    setErrorStatus(status)
    setShowMessageBox(true)
  }

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await getCampaigns()
      setCampaigns(data)
    } catch (e) {
      if (e instanceof Error) {
        showFeedback(
          true,
          e.message,
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    getData()
  }, [getData])

  const copyToClipboard = async (text: string, label: string, key?: string) => {
    if (!text) {
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      if (key) {
        setCopiedKey(key)
        setTimeout(() => {
          setCopiedKey((prev) => (prev === key ? null : prev))
        }, 2000)
      } else {
        showFeedback(false, `${label} copied`)
      }
    } catch {
      showFeedback(true, `Failed to copy ${label.toLowerCase()}`)
    }
  }

  const handleDelete = () => {
    if (!hasEditPermission || isCheck.length === 0) {
      return
    }
    setDeleteConfirmationModal(true)
  }

  const deleteFunc = async () => {
    try {
      setLoading(true)
      await Promise.all(isCheck.map((id) => deleteCampaign(id)))
      setIsCheck([])
      setIsCheckAll(false)
      await getData()
      showFeedback(false, 'Delete Successful')
    } catch (e) {
      if (e instanceof Error) {
        showFeedback(
          true,
          e.message,
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    const next = !isCheckAll
    setIsCheckAll(next)
    setIsCheck(next ? campaigns.map((c) => c.id) : [])
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck((prev) =>
      checked ? [...prev, id] : prev.filter((item) => item !== id),
    )
  }

  const openEdit = (campaign: ICampaign) => {
    if (!hasEditPermission) {
      return
    }
    setCurrentCampaign(campaign)
    setEditMode(true)
    setShowCreateCampaignModal(true)
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'name',
      render: (row) => (
        <div className="text-subtitle-m min-w-[180px] text-monochrome-1">
          {row.name}
        </div>
      ),
    },
    {
      title: 'URL',
      accessor: 'url',
      render: (row) => (
        <div className="text-body-medium min-w-[200px] text-monochrome-2">
          {row.url}
        </div>
      ),
    },
    {
      title: 'Password',
      accessor: 'passwordProtected',
      render: (row) => (
        <Checkbox checked={Boolean(row.passwordProtected)} disabled />
      ),
    },
    {
      title: 'Loyal Users',
      accessor: 'onlyForLoyalUsers',
      render: (row) => (
        <Checkbox checked={Boolean(row.onlyForLoyalUsers)} disabled />
      ),
    },
    {
      title: 'Selected Ticket',
      accessor: 'tickets',
      render: (row) => {
        const tickets = (row.tickets ?? []) as ICampaign['tickets']
        if (tickets.length === 0) {
          return <span className="text-monochrome-2">—</span>
        }
        return (
          <div className="min-w-[220px] max-w-[260px] text-body-small text-monochrome-2">
            {map(tickets, (t) => t.name).join(', ')}
          </div>
        )
      },
    },
    {
      title: 'Status',
      accessor: 'active',
      render: (row) => (
        <span
          className={
            row.active
              ? 'text-accent-1 font-semibold'
              : 'text-monochrome-2 font-semibold'
          }
        >
          {row.active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Copy Link',
      accessor: '',
      render: (row: ICampaign) => {
        const webKey = `${row.id}:web`
        const mobileKey = `${row.id}:mobile`
        const webCopied = copiedKey === webKey
        const mobileCopied = copiedKey === mobileKey
        return (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => copyToClipboard(row.webUrl, 'Web link', webKey)}
              className={
                webCopied
                  ? '!bg-green-600 !border-green-600 !text-white hover:!bg-green-600'
                  : ''
              }
            >
              {webCopied ? 'Copied' : 'Copy Web Link'}
              {!webCopied && (
                <Icon
                  icon="copy"
                  width="16"
                  height="16"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() =>
                copyToClipboard(row.mobileUrl, 'Mobile link', mobileKey)
              }
              className={
                mobileCopied
                  ? '!bg-green-600 !border-green-600 !text-white hover:!bg-green-600'
                  : ''
              }
            >
              {mobileCopied ? 'Copied' : 'Copy Mobile Link'}
              {!mobileCopied && (
                <Icon
                  icon="copy"
                  width="16"
                  height="16"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              )}
            </Button>
          </div>
        )
      },
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            openEdit(row)
          }}
        >
          Edit
        </a>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Campaigns"
        actions={
          <>
            {hasEditPermission && isCheck.length >= 1 && (
              <>
                <ActionsMenu
                  actions={[{ label: 'Delete', onClick: handleDelete }]}
                />

                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </>
            )}
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setEditMode(false)
                  setCurrentCampaign(undefined)
                  setShowCreateCampaignModal(true)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Campaigns" className="mb-5" />
        <Table
          className="[&_th]:!whitespace-nowrap"
          isCheck={isCheck}
          columns={columns}
          data={campaigns}
          alignment="middle"
        />
      </Card>
      <CreateCampaignModal
        showEditModal={showCreateCampaignModal}
        setShowEditModal={setShowCreateCampaignModal}
        campaign={currentCampaign}
        editMode={editMode}
        onSaved={getData}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={isCheck.length > 1 ? 'Campaigns' : 'Campaign'}
        deleteFunc={deleteFunc}
        deletedItem={
          isCheck.length > 1
            ? `${isCheck.length} campaigns`
            : find(campaigns, { id: isCheck[0] })?.name
        }
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
